import { useEffect, useState } from 'react';

import styled from '@emotion/styled';

import StatusCell from '../../../components/Badge/StatusCell';
import { PrimaryBlackButton } from '../../../components/Button/Buttons';
import StatsCard from '../../../components/Cards/StartsCard';
import BarChart from '../../../components/Charts/BarChart';
import PageHeader from '../../../components/Layout/PageHeader';
import PageLayout from '../../../components/Layout/PageLayout';
import Page from '../../../components/Page/Page';
import Table, { TableLabel, TableLabelBox } from '../../../components/Table/Table';
import TableDateTimeFormatter from '../../../components/Table/TableAccessories/TableDateTimeFormatter';
import TableMoneyFormatter from '../../../components/Table/TableAccessories/TableMoneyFormatter';
import useReconciliationHealth from '../../../hooks/dashboard/reconciliation/useReconciliationHealth';
import useRunReconciliation from '../../../hooks/dashboard/reconciliation/useRunReconciliation';
import { formatMinutes } from '../../../models/risk/risk.model';
import { capitalize, formatNaira, formatNumber } from '../../../utils/utils';
import TransactionDetailSlider from '../Transactions/TransactionDetailSlider';

const REFRESH_MS = 30_000;

const STAGE_LABELS: Record<string, string> = {
  charge: 'Card charge unclear',
  vtpass: 'Delivery unclear',
  processing: 'Stuck processing',
  reversal: 'Refund pending',
};

const Reconciliation = () => {
  const { health, fetchHealth } = useReconciliationHealth();
  const { loading: running, runAll } = useRunReconciliation();
  const [tick, setTick] = useState(0);
  const [queueKey, setQueueKey] = useState(0);

  useEffect(() => {
    fetchHealth();
    // eslint-disable-next-line
  }, [tick]);

  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  const reloadAll = () => {
    setTick((t) => t + 1);
    setQueueKey((k) => k + 1);
  };

  const ledgerGap = health
    ? Math.abs(Number(health.ledger_total_debits) - Number(health.ledger_total_credits))
    : 0;
  const ledgerBalanced = !!health && ledgerGap === 0 && health.ledger_imbalanced_transactions === 0;

  const agePoints = (health?.age_buckets ?? []).map((bucket) => ({
    label: bucket.label,
    count: bucket.count,
  }));

  return (
    <Page title='Hethera | Reconciliation'>
      <PageLayout>
        <Container>
          <PageHeader
            title='Reconciliation'
            className='borderless no-bottom-padding'
            customComp={
              <HeaderControls>
                <UpdatedText>
                  {health
                    ? `Updated ${new Date(health.generated_at).toLocaleTimeString()} · auto-reconciles every 2 min`
                    : 'Loading…'}
                </UpdatedText>
                <WorkerPill className={workerClass(health?.worker_alive)}>
                  {workerLabel(health?.worker_alive)}
                </WorkerPill>
                <PrimaryBlackButton
                  type='button'
                  className='large'
                  disabled={running}
                  onClick={() => runAll(reloadAll)}
                >
                  {running ? 'Reconciling…' : 'Run reconciliation now'}
                </PrimaryBlackButton>
              </HeaderControls>
            }
          />

          <StatsGrid>
            <StatsCard.Root>
              <StatsCard.Header>Money in limbo</StatsCard.Header>
              <StatsCard.Value>
                {formatNaira(Number(health?.unresolved_amount ?? 0))}
              </StatsCard.Value>
              <StatsCard.PlainFooter>
                {formatNumber(health?.unresolved_count ?? 0)} purchases with no confirmed outcome
              </StatsCard.PlainFooter>
            </StatsCard.Root>

            <StatsCard.Root>
              <StatsCard.Header>Oldest unresolved</StatsCard.Header>
              <StatsCard.Value>
                <Hot className={(health?.oldest_unresolved_minutes ?? 0) >= 60 ? 'hot' : ''}>
                  {formatMinutes(health?.oldest_unresolved_minutes)}
                </Hot>
              </StatsCard.Value>
              <StatsCard.PlainFooter>
                {formatNumber(health?.escalated_count ?? 0)} escalated (24h+)
              </StatsCard.PlainFooter>
            </StatsCard.Root>

            <StatsCard.Root>
              <StatsCard.Header>Refunds owed</StatsCard.Header>
              <StatsCard.Value>
                {formatNaira(Number(health?.reversal_pending_amount ?? 0))}
              </StatsCard.Value>
              <StatsCard.PlainFooter>
                {formatNumber(health?.reversal_pending_count ?? 0)} in REVERSAL_PENDING
              </StatsCard.PlainFooter>
            </StatsCard.Root>

            <StatsCard.Root>
              <StatsCard.Header>Auto-settled (24h)</StatsCard.Header>
              <StatsCard.Value>{formatNumber(health?.auto_resolved_24h ?? 0)}</StatsCard.Value>
              <StatsCard.PlainFooter>
                Median time to settle{' '}
                {health?.median_resolution_minutes != null
                  ? formatMinutes(Math.round(health.median_resolution_minutes))
                  : '—'}
                {health?.late_refunds_24h ? ` · ${health.late_refunds_24h} late refunds` : ''}
              </StatsCard.PlainFooter>
            </StatsCard.Root>
          </StatsGrid>

          <TwoColumn>
            <Card>
              <TableLabelBox>
                <TableLabel>Ledger integrity</TableLabel>
              </TableLabelBox>
              <LedgerBody>
                <StatusCell
                  data={{ state: ledgerBalanced ? 'HEALTHY' : 'CRITICAL' }}
                  statusKey='state'
                />
                <LedgerText>
                  {ledgerBalanced
                    ? 'Every transaction balances. Debits equal credits.'
                    : `${health?.ledger_imbalanced_transactions ?? 0} transaction(s) out of balance — gap ${formatNaira(ledgerGap)}. See Risk Center → Ledger debits ≠ credits.`}
                </LedgerText>
                <LedgerRow>
                  <span>Total debits</span>
                  <strong>{formatNaira(Number(health?.ledger_total_debits ?? 0))}</strong>
                </LedgerRow>
                <LedgerRow>
                  <span>Total credits</span>
                  <strong>{formatNaira(Number(health?.ledger_total_credits ?? 0))}</strong>
                </LedgerRow>
              </LedgerBody>
              <TableLabelBox>
                <TableLabel>Where it's stuck</TableLabel>
              </TableLabelBox>
              <LedgerBody>
                {Object.entries(health?.by_stage ?? {}).map(([stage, count]) => (
                  <LedgerRow key={stage}>
                    <span>{STAGE_LABELS[stage] ?? stage}</span>
                    <strong>{formatNumber(count)}</strong>
                  </LedgerRow>
                ))}
              </LedgerBody>
            </Card>

            <Card>
              <TableLabelBox>
                <TableLabel>Unresolved purchases by age</TableLabel>
              </TableLabelBox>
              <BarChart
                data={agePoints}
                dataKeys={['count']}
                xAxisDataKey='label'
                fills={['#1F7A45']}
                tooltipLabel={['Purchases']}
                showLegend={false}
                aspect={2}
              />
            </Card>
          </TwoColumn>

          <Card>
            <TableLabelBox>
              <TableLabel>Provider health (last 24h)</TableLabel>
            </TableLabelBox>
            <Table
              tableHeader={{
                Provider: 'provider',
                Status: 'status',
                'Success rate': 'success_rate',
                Attempts: 'attempts',
                Failed: 'failed',
                Unresolved: 'unresolved',
                'Failures (1h)': 'failures_last_hour',
              }}
              dummyData={health?.providers ?? []}
              showPaginationControl={false}
              cellsCustomComp={{
                status: <StatusCell statusKey='status' hasDot />,
                success_rate: <PercentCell dataKey='success_rate' />,
              }}
              emptyStateMsg='No provider traffic in the last 24 hours'
            />
          </Card>

          <Card>
            <TableLabelBox>
              <TableLabel>Reconciliation queue (oldest first)</TableLabel>
            </TableLabelBox>
            <Table
              reloadSignal={queueKey}
              tableHeader={{
                Reference: 'reference',
                Customer: 'chat_id',
                Stage: 'stage',
                Status: 'status',
                Amount: 'amount',
                Age: 'age_minutes',
                Escalated: 'escalated',
                Created: 'created_at',
              }}
              tableDataSourceUrl='api/v1/admin/reconciliation/queue'
              size={20}
              tableFilterBoxFeatures={{
                showSearchBox: true,
                searchBoxPlaceholder: 'Search reference, customer or provider reference',
              }}
              cellsCustomComp={{
                stage: <StageCell dataKey='stage' />,
                status: <StatusCell statusKey='status' hasDot positiveType />,
                amount: <TableMoneyFormatter dataKey='amount' />,
                age_minutes: <AgeCell dataKey='age_minutes' />,
                escalated: <EscalatedCell dataKey='escalated' />,
                created_at: <TableDateTimeFormatter dataKey='created_at' />,
              }}
              clickRowShowSliderComp={(row) => (
                <TransactionDetailSlider data={row} onReconciled={reloadAll} />
              )}
              hasClickableRow
              emptyStateMsg='Queue is empty. Every purchase has a confirmed outcome'
            />
          </Card>
        </Container>
      </PageLayout>
    </Page>
  );
};

export default Reconciliation;

const workerClass = (alive?: boolean | null) =>
  alive === true ? 'healthy' : alive === false ? 'down' : 'unknown';

const workerLabel = (alive?: boolean | null) =>
  alive === true
    ? '● Worker running'
    : alive === false
      ? '● Worker down: cron is not reconciling'
      : '● Worker status unknown';

interface CellProps {
  data?: Record<string, any>;
  dataKey?: string;
}

const value = ({ data, dataKey }: CellProps) => (data && dataKey ? data[dataKey] : undefined);

const PercentCell: React.FC<CellProps> = (props) => <>{Number(value(props) ?? 0).toFixed(1)}%</>;

const StageCell: React.FC<CellProps> = (props) => {
  const stage = String(value(props) ?? '');
  return <>{STAGE_LABELS[stage] ?? capitalize(stage)}</>;
};

const AgeCell: React.FC<CellProps> = (props) => {
  const minutes = Number(value(props) ?? 0);
  return <Hot className={minutes >= 60 ? 'hot' : ''}>{formatMinutes(minutes)}</Hot>;
};

const EscalatedCell: React.FC<CellProps> = (props) => (value(props) ? <Hot className='hot'>Yes</Hot> : <>—</>);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const UpdatedText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.grayTextColor};
`;

const WorkerPill = styled.span`
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 8px;

  &.healthy {
    color: ${({ theme }) => theme.palette.greenColor};
    background-color: rgba(31, 122, 69, 0.05);
  }

  &.down {
    color: white;
    background-color: ${({ theme }) => theme.palette.errorRed};
  }

  &.unknown {
    color: ${({ theme }) => theme.palette.grayTextColor};
    background-color: ${({ theme }) => theme.palette.veryLightGrayBg};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
`;

const Card = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: var(--radius);
  padding: 24px;
  background-color: white;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LedgerBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const LedgerText = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.palette.secondaryTextColor};
`;

const LedgerRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: ${({ theme }) => theme.palette.primaryTextColor};
`;

const Hot = styled.span`
  &.hot {
    color: ${({ theme }) => theme.palette.errorRed};
    font-weight: 600;
  }
`;
